// Issue invoice + send payment link (docs/21 §8) — coordinator/agency-admin action.
// POST /billing/payment-links → for the nsofar-agency rail, nsofar mints a Stripe link;
// HCM returns it here. The coordinator can copy/share it; in non-test_mode the backend
// also best-effort sends it to the family over WhatsApp.

import { useState, useEffect } from "react";
import type { JSX, FormEvent } from "react";
import { toast } from "sonner";
import { apiFetch } from "../lib/api.js";
import { Modal } from "./Modal.js";
import { FormField } from "./FormField.js";
import { LineItemsEditor, computeLineItems, type LineItemRow } from "./LineItemsEditor.js";

interface Props {
  onClose: () => void;
  onIssued: () => void;
}

interface PaymentLinkResult {
  invoice_id: string;
  invoice_number: string;
  nsofar_invoice_number: string;
  payment_link: string;
  test_mode: boolean;
  sent_via: string | null;
}

const CURRENCIES = ["USD", "INR", "GBP", "EUR", "AED"];
const SEL_CLS = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-navy-500";

interface FieldErrors { patient_id?: string; amount?: string }

function validate(patientId: string, amountMinor: number): FieldErrors {
  const errs: FieldErrors = {};
  if (patientId.trim().length !== 36) errs.patient_id = "Enter a valid patient UUID.";
  if (!Number.isFinite(amountMinor) || amountMinor <= 0) errs.amount = "Enter an amount greater than 0, or add line items.";
  return errs;
}

interface SubmitArgs {
  patientId: string; amount: string; currency: string; description: string;
  dueDate: string; lineItems: LineItemRow[];
  setErrors: (e: FieldErrors) => void; setApiError: (e: string | null) => void;
  setSaving: (b: boolean) => void; setResult: (r: PaymentLinkResult) => void;
  onIssuedToast: () => void;
}

// When itemised, the line-item total is the authoritative amount (matches the backend's sum check).
function buildBody(a: SubmitArgs): { body: Record<string, unknown>; amountMinor: number } {
  const { lineItems, totalMinor } = computeLineItems(a.lineItems);
  const hasLines = lineItems.length > 0;
  const amountMinor = hasLines ? totalMinor : Math.round(Number(a.amount) * 100);
  const body: Record<string, unknown> = {
    patient_id: a.patientId.trim(), amount_minor: amountMinor, currency: a.currency,
    ...(a.description.trim() ? { description: a.description.trim() } : {}),
    ...(a.dueDate ? { due_date: a.dueDate } : {}),
    ...(hasLines ? { line_items: lineItems } : {}),
  };
  return { body, amountMinor };
}

async function submit(a: SubmitArgs): Promise<void> {
  a.setApiError(null);
  const { body, amountMinor } = buildBody(a);
  const errs = validate(a.patientId, amountMinor);
  if (Object.keys(errs).length > 0) { a.setErrors(errs); return; }
  a.setErrors({}); a.setSaving(true);
  try {
    const res = await apiFetch<PaymentLinkResult>("/billing/payment-links", { method: "POST", body: JSON.stringify(body) });
    a.setResult(res);
    a.onIssuedToast();
  } catch (err) {
    a.setApiError(err instanceof Error ? err.message : "Failed to issue payment link.");
  } finally { a.setSaving(false); }
}

interface FormViewProps {
  patientId: string; setPatientId: (v: string) => void;
  amount: string; setAmount: (v: string) => void;
  currency: string; setCurrency: (v: string) => void;
  description: string; setDescription: (v: string) => void;
  dueDate: string; setDueDate: (v: string) => void;
  lineItems: LineItemRow[]; setLineItems: (v: LineItemRow[]) => void;
  errors: FieldErrors; apiError: string | null; saving: boolean;
  onSubmit: (e: FormEvent) => void; onClose: () => void;
}

// Amount field: editable when there are no line items; otherwise derived (read-only) from them.
function AmountField(p: FormViewProps): JSX.Element {
  const { totalMinor, lineItems } = computeLineItems(p.lineItems);
  if (lineItems.length > 0) {
    return <FormField label="Amount (from line items)" value={(totalMinor / 100).toFixed(2)} onChange={() => { /* derived */ }} disabled />;
  }
  return <FormField label="Amount" type="number" value={p.amount} onChange={p.setAmount} required placeholder="0.00" error={p.errors.amount} />;
}

function FormView(p: FormViewProps): JSX.Element {
  return (
    <form onSubmit={p.onSubmit} noValidate className="space-y-4">
      <FormField label="Patient ID" value={p.patientId} onChange={p.setPatientId} required
        placeholder="Patient UUID" error={p.errors.patient_id} />
      <div className="flex gap-3">
        <div className="flex-1"><AmountField {...p} /></div>
        <div className="w-28">
          <label htmlFor="pl-currency" className="block text-sm font-medium text-slate-700 mb-1">Currency</label>
          <select id="pl-currency" value={p.currency} onChange={(e) => p.setCurrency(e.target.value)} className={SEL_CLS}>
            {(CURRENCIES.includes(p.currency) ? CURRENCIES : [p.currency, ...CURRENCIES]).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label htmlFor="pl-due" className="block text-sm font-medium text-slate-700 mb-1">Due date (optional)</label>
        <input id="pl-due" type="date" value={p.dueDate} onChange={(e) => p.setDueDate(e.target.value)} className={SEL_CLS} />
      </div>
      <div>
        <label htmlFor="pl-desc" className="block text-sm font-medium text-slate-700 mb-1">Description</label>
        <textarea id="pl-desc" value={p.description} onChange={(e) => p.setDescription(e.target.value)} rows={2}
          placeholder="e.g. Medicines reimbursement"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-500 resize-none" />
      </div>
      <LineItemsEditor rows={p.lineItems} onChange={p.setLineItems} />
      {p.apiError && <p role="alert" className="text-sm text-red-600">{p.apiError}</p>}
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={p.onClose}
          className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">Cancel</button>
        <button type="submit" disabled={p.saving}
          className="px-4 py-2 text-sm rounded-lg bg-navy-700 text-white font-medium hover:bg-navy-800 disabled:opacity-50">
          {p.saving ? "Issuing…" : "Issue + get link"}
        </button>
      </div>
    </form>
  );
}

function sentViaLabel(sentVia: string | null, testMode: boolean): string {
  if (testMode) return "Test mode — nothing was sent to the family. Share the link manually.";
  if (sentVia === "whatsapp") return "Sent to the family over WhatsApp.";
  return "Not auto-sent — share the link with the family directly.";
}

function ResultView({ result, onClose }: { result: PaymentLinkResult; onClose: () => void }): JSX.Element {
  function copy(): void {
    void navigator.clipboard.writeText(result.payment_link).then(() => toast.success("Link copied"));
  }
  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
        Invoice {result.invoice_number} issued{result.test_mode ? " (test mode)" : ""}. nsofar #{result.nsofar_invoice_number}.
      </div>
      <div>
        <label htmlFor="pl-link" className="block text-sm font-medium text-slate-700 mb-1">Payment link</label>
        <div className="flex gap-2">
          <input id="pl-link" readOnly value={result.payment_link}
            className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 bg-slate-50" />
          <button type="button" onClick={copy}
            className="px-3 py-2 text-sm rounded-lg bg-navy-700 text-white font-medium hover:bg-navy-800">Copy</button>
        </div>
      </div>
      <p className="text-xs text-slate-500">{sentViaLabel(result.sent_via, result.test_mode)}</p>
      <div className="flex justify-end pt-2">
        <button type="button" onClick={onClose}
          className="px-4 py-2 text-sm rounded-lg bg-navy-700 text-white font-medium hover:bg-navy-800">Done</button>
      </div>
    </div>
  );
}

export function PaymentLinkModal({ onClose, onIssued }: Props): JSX.Element {
  const [patientId, setPatientId] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [lineItems, setLineItems] = useState<LineItemRow[]>([]);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<PaymentLinkResult | null>(null);

  // Default the currency to the agency's configured default (e.g. INR) instead of hardcoded USD —
  // coordinators think in their agency's currency. Falls back to USD if unconfigured/unreadable.
  useEffect(() => {
    apiFetch<{ default_currency?: string }>("/billing/payment-config")
      .then((cfg) => { if (cfg.default_currency) setCurrency(cfg.default_currency); })
      .catch(() => { /* keep the USD fallback */ });
  }, []);

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    void submit({
      patientId, amount, currency, description, dueDate, lineItems,
      setErrors, setApiError, setSaving, setResult,
      onIssuedToast: () => { toast.success("Payment link issued"); onIssued(); },
    });
  }

  return (
    <Modal title="Issue invoice + send link" onClose={onClose}>
      {result
        ? <ResultView result={result} onClose={onClose} />
        : <FormView
            patientId={patientId} setPatientId={setPatientId}
            amount={amount} setAmount={setAmount}
            currency={currency} setCurrency={setCurrency}
            description={description} setDescription={setDescription}
            dueDate={dueDate} setDueDate={setDueDate}
            lineItems={lineItems} setLineItems={setLineItems}
            errors={errors} apiError={apiError} saving={saving}
            onSubmit={onSubmit} onClose={onClose} />}
    </Modal>
  );
}
